import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { User, Lock, Mail, AlertCircle, Calendar, Coins, LogOut, Upload, Trash2, Camera, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { getUserCoins } from '../lib/supabase';

export function Account() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userCoins, setUserCoins] = useState<number>(0);
  const [loadingCoins, setLoadingCoins] = useState(true);
  const [avatar, setAvatar] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [joinDate, setJoinDate] = useState<string>('');

  const loadUserCoins = async (userId: string) => {
    if (!userId) return;
    setLoadingCoins(true);
    try {
      const coinsData = await getUserCoins(userId);
      setUserCoins(coinsData?.balance || 0);
    } catch (error) {
      console.error('Error al cargar las monedas en Account:', error);
    } finally {
      setLoadingCoins(false);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUser(user);
          
          const createdAt = new Date(user.created_at);
          const options: Intl.DateTimeFormatOptions = { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          };
          setJoinDate(createdAt.toLocaleDateString('es-ES', options));
          
          await loadUserCoins(user.id);
          
          await getAvatar(user.id);
        } else {
          setLoadingCoins(false);
        }
      } catch (error) {
        console.error('Error al obtener usuario:', error);
        setLoadingCoins(false);
      }
    };
    
    fetchInitialData();

    const handleCoinsUpdate = () => {
      setUser(currentUser => {
        if (currentUser?.id) {
          loadUserCoins(currentUser.id);
        }
        return currentUser;
      });
    };

    window.addEventListener('coinsUpdated', handleCoinsUpdate);

    return () => {
      window.removeEventListener('coinsUpdated', handleCoinsUpdate);
    };
  }, []);

  const getAvatar = async (userId: string) => {
    try {
      const { data: existingAvatar, error: existingError } = await supabase
        .storage
        .from('curriculab')
        .list(`avatars/${userId}`);
      
      if (existingError) throw existingError;
      
      if (existingAvatar && existingAvatar.length > 0) {
        const { data: { publicUrl } } = supabase
          .storage
          .from('curriculab')
          .getPublicUrl(`avatars/${userId}/${existingAvatar[0].name}`);
          
        setAvatar(publicUrl);
      }
    } catch (error) {
      console.error('Error al obtener avatar:', error);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!user?.email) {
      toast.error('No se pudo obtener la información del usuario.');
      setLoading(false);
      return;
    }

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (signInError) {
        toast.error('La contraseña actual es incorrecta.');
        setLoading(false);
        return;
      }
      
    } catch (verificationError) {
      console.error('Error verificando la contraseña actual:', verificationError);
      toast.error('Error al verificar la contraseña actual.');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres.');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Las nuevas contraseñas no coinciden.');
      setLoading(false);
      return;
    }

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;
      
      toast.success('Contraseña actualizada correctamente');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordChange(false);

    } catch (error: any) {
      toast.error(error.message || 'Error al actualizar la contraseña.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const uploadAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }
      
      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `avatars/${user.id}/${fileName}`;
      
      setUploading(true);
      
      await deleteAvatar();
      
      const { error: uploadError } = await supabase.storage
        .from('curriculab')
        .upload(filePath, file);
        
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase
        .storage
        .from('curriculab')
        .getPublicUrl(filePath);
        
      setAvatar(publicUrl);
      toast.success('Foto de perfil actualizada');
    } catch (error: any) {
      toast.error('Error al subir la imagen');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const deleteAvatar = async () => {
    try {
      if (!user) return;
      
      setUploading(true);
      
      const { data: existingFiles } = await supabase
        .storage
        .from('curriculab')
        .list(`avatars/${user.id}`);
        
      if (existingFiles && existingFiles.length > 0) {
        const filesToRemove = existingFiles.map(file => `avatars/${user.id}/${file.name}`);
        
        const { error } = await supabase
          .storage
          .from('curriculab')
          .remove(filesToRemove);
          
        if (error) throw error;
      }
      
      setAvatar(null);
      toast.success('Foto de perfil eliminada');
    } catch (error) {
      toast.error('Error al eliminar la imagen');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleCancelPasswordChange = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordChange(false);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black pt-16 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4 sm:p-6 shadow-lg">
          <h1 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-4 flex items-center gap-2">
            <User className="w-6 h-6 text-purple-400" />
            Mi cuenta
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="md:col-span-1">
              <div className="bg-white/5 rounded-xl border border-white/10 p-4 flex flex-col items-center">
                <div className="relative group mb-3">
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden bg-gray-800 border-2 border-purple-500/30 flex items-center justify-center relative">
                    {avatar ? (
                      <img 
                        src={avatar} 
                        alt="Avatar" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-12 h-12 text-gray-400" />
                    )}
                    
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button 
                        onClick={() => fileInputRef.current?.click()}
                        className="p-2 bg-purple-500/80 rounded-full text-white hover:bg-purple-600/80 transition-colors"
                        disabled={uploading}
                      >
                        <Camera className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={uploadAvatar}
                    accept="image/*"
                    className="hidden"
                    disabled={uploading}
                  />
                </div>
                
                <div className="flex flex-col items-center">
                  <h2 className="text-lg font-semibold text-white mb-1">
                    {user.user_metadata?.full_name || user.email.split('@')[0]}
                  </h2>
                  <p className="text-gray-400 text-sm mb-2">{user.email}</p>
                  
                  {avatar && (
                    <button
                      onClick={deleteAvatar}
                      className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 mt-1"
                      disabled={uploading}
                    >
                      <Trash2 className="w-3 h-3" />
                      <span>Eliminar foto</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="md:col-span-2 space-y-4">
              <div className="bg-white/5 rounded-xl border border-white/10 p-4">
                <h2 className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-3">
                  Información de la cuenta
                </h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="flex items-center gap-2 text-gray-300 bg-white/5 rounded-lg p-2 border border-white/5">
                    <Mail className="w-4 h-4 text-purple-400 flex-shrink-0" />
                    <div className="overflow-hidden">
                      <p className="text-xs text-gray-400">Correo</p>
                      <p className="text-sm truncate">{user.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-gray-300 bg-white/5 rounded-lg p-2 border border-white/5">
                    <Calendar className="w-4 h-4 text-purple-400 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400">Miembro desde</p>
                      <p className="text-sm">{joinDate}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-gray-300 bg-white/5 rounded-lg p-2 border border-white/5">
                    <Coins className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400">Balance</p>
                      <p className="text-sm flex items-center">
                        {loadingCoins ? (
                          <span className="text-gray-400 text-xs">Cargando...</span>
                        ) : (
                          <>
                            <span className="text-yellow-400 font-bold mr-1">{userCoins}</span>
                            <span className="text-yellow-400">⚡</span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/5 rounded-xl border border-white/10 p-4">
                <h2 className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-3">
                  Acciones de la cuenta
                </h2>
                
                <div className="space-y-3">
                  {!showPasswordChange ? (
                    <button
                      onClick={() => setShowPasswordChange(true)}
                      className="w-full py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg transition-colors flex items-center justify-between px-3"
                    >
                      <div className="flex items-center">
                        <Lock className="w-4 h-4 text-purple-400 mr-2" />
                        <span className="text-sm">Cambiar contraseña</span>
                      </div>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <form onSubmit={handlePasswordChange} className="bg-white/5 rounded-lg border border-white/10 p-3">
                      <h3 className="text-base font-medium text-white mb-3 flex items-center gap-2">
                        <Lock className="w-4 h-4 text-purple-400" />
                        Cambiar contraseña
                      </h3>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">
                            Contraseña Actual
                          </label>
                          <div className="relative">
                            <input
                              type={showCurrentPassword ? "text" : "password"}
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              className="w-full px-3 py-1.5 pr-10 bg-white/5 border border-white/10 rounded-lg focus:border-purple-500 text-white text-sm"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                              aria-label={showCurrentPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                            >
                              {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">
                            Nueva contraseña
                          </label>
                          <div className="relative">
                            <input
                              type={showNewPassword ? "text" : "password"}
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              className="w-full px-3 py-1.5 pr-10 bg-white/5 border border-white/10 rounded-lg focus:border-purple-500 text-white text-sm"
                              minLength={6}
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                              aria-label={showNewPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                            >
                              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">
                            Confirmar contraseña
                          </label>
                          <div className="relative">
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              className="w-full px-3 py-1.5 pr-10 bg-white/5 border border-white/10 rounded-lg focus:border-purple-500 text-white text-sm"
                              minLength={6}
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                              aria-label={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                            >
                              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 text-xs text-yellow-400 mt-1">
                          <AlertCircle className="w-3 h-3" />
                          <span>La contraseña debe tener al menos 6 caracteres</span>
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleCancelPasswordChange}
                            className="flex-1 py-1.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                          >
                            Cancelar
                          </button>
                          <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 text-sm"
                          >
                            {loading ? 'Actualizando...' : 'Actualizar'}
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                  
                  <button
                    onClick={handleSignOut}
                    className="w-full py-2 bg-red-500/20 hover:bg-red-500/30 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Cerrar sesión</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}